import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Compass, ArrowRight, Loader2, HelpCircle, Wand2, Sparkles, Cog, GitCompare, Brain } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ANGLES = [
  { key: "pourquoi", Icon: HelpCircle, label: { fr: "Pourquoi", en: "Why", es: "Por qué", nl: "Waarom" }, instr: { fr: "En cherchant le pourquoi profond :", en: "Digging into the deep why:", es: "Buscando el porqué profundo:", nl: "Op zoek naar het diepe waarom:" } },
  { key: "etsi", Icon: Wand2, label: { fr: "Et si", en: "What if", es: "Y si", nl: "Wat als" }, instr: { fr: "En imaginant « et si… » :", en: "Imagining “what if…”:", es: "Imaginando «y si…»:", nl: "Door je “wat als…” voor te stellen:" } },
  { key: "imaginer", Icon: Sparkles, label: { fr: "Imaginer", en: "Imagine", es: "Imaginar", nl: "Verbeelden" }, instr: { fr: "En laissant libre cours à l'imagination :", en: "Letting imagination run free:", es: "Dejando volar la imaginación:", nl: "Door de verbeelding vrij spel te geven:" } },
  { key: "comment", Icon: Cog, label: { fr: "Comment", en: "How", es: "Cómo", nl: "Hoe" }, instr: { fr: "En explorant le comment :", en: "Exploring the how:", es: "Explorando el cómo:", nl: "Door het hoe te verkennen:" } },
  { key: "comparer", Icon: GitCompare, label: { fr: "Comparer", en: "Compare", es: "Comparar", nl: "Vergelijken" }, instr: { fr: "En comparant avec autre chose :", en: "Comparing with something else:", es: "Comparando con otra cosa:", nl: "Door met iets anders te vergelijken:" } },
  { key: "philosopher", Icon: Brain, label: { fr: "Philosopher", en: "Philosophise", es: "Filosofar", nl: "Filosoferen" }, instr: { fr: "En prenant de la hauteur, philosophiquement :", en: "Taking a step back, philosophically:", es: "Tomando distancia, filosóficamente:", nl: "Filosofisch, met wat afstand:" } },
];

const L = {
  fr: {
    eyebrow: "L'art de se laisser surprendre",
    title: "La Boussole de l'Inattendu",
    intro: "Choisis un angle, laisse surgir une question à laquelle tu n'aurais jamais pensé — puis creuse-la dans Le Fil.",
    dailyLabel: "La question du jour",
    chooseAngle: "Choisis un angle (facultatif)",
    creuser: "Creuser cette question",
    cta: "Explorer la Boussole",
    loading: "La Boussole cherche le nord de l'inattendu…",
  },
  en: {
    eyebrow: "The art of being surprised",
    title: "The Compass of the Unexpected",
    intro: "Pick an angle, let a question you'd never have thought of surface — then dig into it in The Thread.",
    dailyLabel: "Question of the day",
    chooseAngle: "Pick an angle (optional)",
    creuser: "Dig into this question",
    cta: "Explore the Compass",
    loading: "The Compass is finding the north of the unexpected…",
  },
  es: {
    eyebrow: "El arte de dejarse sorprender",
    title: "La Brújula de lo Inesperado",
    intro: "Elige un ángulo, deja surgir una pregunta que nunca habrías imaginado — y luego profundízala en El Hilo.",
    dailyLabel: "La pregunta del día",
    chooseAngle: "Elige un ángulo (opcional)",
    creuser: "Profundizar esta pregunta",
    cta: "Explorar la Brújula",
    loading: "La Brújula busca el norte de lo inesperado…",
  },
  nl: {
    eyebrow: "De kunst van het verrast worden",
    title: "Het Kompas van het Onverwachte",
    intro: "Kies een invalshoek, laat een vraag opduiken waar je nooit aan gedacht zou hebben — en graaf er dan in door via De Draad.",
    dailyLabel: "Vraag van de dag",
    chooseAngle: "Kies een invalshoek (optioneel)",
    creuser: "Deze vraag uitdiepen",
    cta: "Ontdek het Kompas",
    loading: "Het Kompas zoekt het noorden van het onverwachte…",
  },
};

export const BoussoleIntro = () => {
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const lang = (i18n.language || "fr").slice(0, 2);
  const c = L[lang] || L.fr;
  const [daily, setDaily] = useState(null);
  const [loading, setLoading] = useState(true);
  const [angle, setAngle] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/curiosity/daily", { params: { lang } })
      .then(({ data }) => { if (alive) setDaily(data); })
      .catch(() => { if (alive) setDaily(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [lang]);

  const creuser = (question) => {
    if (!question) return;
    const instr = angle ? ANGLES.find((a) => a.key === angle)?.instr[lang] : null;
    const prompt = instr ? `${instr} ${question}` : question;
    try { localStorage.setItem("coccon_pending_prompt", prompt); } catch { /* ignore */ }
    navigate(user ? "/atelier" : "/entrer");
  };

  return (
    <section className="relative px-6 my-20" data-testid="boussole-intro-section">
      <div className="max-w-4xl mx-auto">
        <div className="relative rounded-[2rem] overflow-hidden border border-primary/20 frame-pulse glass">
          <div aria-hidden className="absolute inset-0 pointer-events-none">
            <div className="absolute left-1/2 top-24 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-primary/10 blur-[120px]" />
            {[...Array(6)].map((_, i) => (
              <motion.span key={i} className="absolute w-1.5 h-1.5 rounded-full bg-primary/40"
                style={{ left: `${12 + i * 14}%`, top: `${20 + (i % 3) * 22}%` }}
                animate={{ y: [0, -18, 0], opacity: [0.2, 0.7, 0.2] }}
                transition={{ duration: 4 + i, repeat: Infinity, delay: i * 0.5 }} />
            ))}
          </div>

          <div className="relative z-10 px-6 sm:px-12 py-14">
            <div className="max-w-3xl mx-auto text-center">
              <p className="inline-flex items-center gap-2 text-primary/80 tracking-[0.25em] uppercase text-xs mb-4">
                <Compass size={14} className="animate-[spin_9s_linear_infinite]" /> {c.eyebrow}
              </p>
              <h2 className="font-display text-4xl sm:text-6xl text-gradient-orange leading-[1.12] pb-[0.08em]" data-testid="boussole-intro-title">{c.title}</h2>
              <p className="mt-4 text-base text-foreground/60 max-w-xl mx-auto">{c.intro}</p>

              <div className="mt-9 max-w-2xl mx-auto min-h-[150px] flex items-center justify-center">
                <AnimatePresence mode="wait">
                  {loading && (
                    <motion.p key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                      className="font-display italic text-foreground/45 text-lg flex items-center gap-3" data-testid="boussole-intro-loading">
                      <Loader2 className="animate-spin text-primary" size={20} /> {c.loading}
                    </motion.p>
                  )}
                  {!loading && daily?.question && (
                    <motion.div
                      key={daily.question}
                      initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.6 }}
                      className="w-full rounded-3xl border border-primary/25 bg-white/[0.035] backdrop-blur-xl p-7 sm:p-9 shadow-2xl"
                      data-testid="boussole-intro-daily"
                    >
                      <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-primary/70 mb-4">
                        <Compass size={13} /> {c.dailyLabel}
                      </span>
                      <p className="font-display italic text-gradient-orange text-2xl sm:text-3xl leading-snug" data-testid="boussole-intro-question">
                        « {daily.question} »
                      </p>
                      {daily.spark && (
                        <p className="mt-4 text-foreground/60 leading-relaxed text-[15px]" data-testid="boussole-intro-spark">{daily.spark}</p>
                      )}
                      <div className="mt-6">
                        <p className="text-[11px] uppercase tracking-[0.18em] text-foreground/40 mb-2.5">{c.chooseAngle}</p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 max-w-md mx-auto" data-testid="boussole-intro-angles">
                          {ANGLES.map(({ key, Icon, label }) => (
                            <button
                              key={key}
                              data-testid={`boussole-intro-angle-${key}`}
                              onClick={() => setAngle(angle === key ? null : key)}
                              className={`flex items-center justify-center gap-2 h-11 px-3 rounded-xl border text-sm transition-colors ${angle === key ? "border-primary bg-primary/15 text-primary" : "border-primary/35 bg-white/[0.035] text-foreground/70 hover:border-primary hover:text-primary"}`}
                              style={{ boxShadow: "inset 0 0 0 1px hsl(38 90% 55% / 0.06)" }}
                            >
                              <Icon size={15} /> {label[lang] || label.fr}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="mt-6 flex justify-center">
                        <button
                          onClick={() => creuser(daily.question)}
                          data-testid="boussole-intro-creuser"
                          className="btn-frost-orange inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold"
                        >
                          {c.creuser} <ArrowRight size={17} />
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <div className="mt-9 flex justify-center">
                <Link to="/entrer" data-testid="boussole-intro-cta"
                  className="btn-frost-orange inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold">
                  {c.cta} <ArrowRight size={17} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
